/* ==========================================================================
   Bedrock — BDR Drawer Cart (bdr-drawer-cart)
   ========================================================================== */

(function () {
  'use strict';

  if (customElements.get('bdr-drawer-cart')) return;

  class BdrDrawerCart extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog) return;

      this.onCartUpdated = this.handleCartUpdated.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onOpenRequest = () => this.open();
      this.onDocumentClick = (event) => {
        const trigger = event.target.closest('[data-bdr-cart-trigger]');
        if (!trigger) return;
        event.preventDefault();
        this.open();
      };
      this.onSubmit = this.handleSubmit.bind(this);

      document.addEventListener('bdr:cart:updated', this.onCartUpdated);
      document.addEventListener('bdr:cart:open', this.onOpenRequest);
      document.addEventListener('click', this.onDocumentClick);
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
    }

    disconnectedCallback() {
      document.removeEventListener('bdr:cart:updated', this.onCartUpdated);
      document.removeEventListener('bdr:cart:open', this.onOpenRequest);
      document.removeEventListener('click', this.onDocumentClick);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      this.shippingController?.abort();
    }

    /* Le tiroir est rendu au chargement de la page. Si le panier bouge ensuite par
       un chemin qui ne passe pas par bdr:cart:updated (fiche produit Dawn, sticky
       ATC, guide), son contenu — barre livraison offerte comprise — reste sur
       l'etat initial. On le rafraichit donc avant chaque ouverture. Impossible de
       le faire apres : refresh() remplace le contenu, dialogue compris. */
    async open() {
      await this.refresh();
      this.show();
    }

    show() {
      if (this.dialog && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
        if (window.Bedrock && typeof window.Bedrock.trapFocus === 'function') {
          this.releaseFocus = window.Bedrock.trapFocus(this.dialog);
        }
      }
    }

    close() {
      if (this.dialog && this.dialog.open) {
        this.dialog.close();
        if (this.releaseFocus) {
          this.releaseFocus();
          this.releaseFocus = null;
        }
      }
    }

    async refresh() {
      try {
        const sectionId = this.dataset.bdrSectionId || 'bdr-drawer-cart';
        const html = await window.Bedrock.fetchSection(sectionId);
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector('bdr-drawer-cart');
        if (newDrawer) {
          this.innerHTML = newDrawer.innerHTML;
          this.dialog = this.querySelector('dialog');
        }
      } catch (e) {
        console.error('Drawer cart refresh failed', e);
      }
    }

    handleCartUpdated() {
      this.refresh().then(() => this.show());
    }

    async handleSubmit(event) {
      const form = event.target.closest('[data-bdr-cart-note-form], [data-bdr-cart-discount-form], [data-bdr-shipping-form]');
      if (!form) return;
      event.preventDefault();
      if (form.hasAttribute('data-bdr-shipping-form')) {
        this.estimateShipping(form);
        return;
      }
      const submit = form.querySelector('[type="submit"]');
      const status = form.querySelector('[data-bdr-cart-form-status]');
      const formData = new FormData(form);
      const payload = {};

      if (form.hasAttribute('data-bdr-cart-note-form')) {
        payload.note = formData.get('note') || '';
      } else {
        payload.discount = String(formData.get('discount') || '').trim();
        if (!payload.discount) return;
      }

      if (submit) submit.disabled = true;
      if (status) status.textContent = '';
      try {
        const response = await fetch(this.dataset.bdrUpdateUrl || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!response.ok) throw new Error(`Cart update ${response.status}`);
        const cart = await response.json();
        document.dispatchEvent(new CustomEvent('bdr:cart:updated', { detail: cart, bubbles: true }));
      } catch (error) {
        if (status) status.textContent = form.dataset.bdrError || 'Impossible de mettre à jour le panier.';
      } finally {
        if (submit) submit.disabled = false;
      }
    }

    async estimateShipping(form) {
      const submit = form.querySelector('[type="submit"]');
      const status = form.querySelector('[data-bdr-cart-form-status]');
      const results = form.querySelector('[data-bdr-shipping-results]');
      const values = new FormData(form);
      const params = new URLSearchParams();
      params.set('shipping_address[country]', String(values.get('country') || ''));
      params.set('shipping_address[zip]', String(values.get('zip') || ''));
      if (values.get('province')) params.set('shipping_address[province]', String(values.get('province')));
      const root = window.Shopify?.routes?.root || '/';

      this.shippingController?.abort();
      this.shippingController = new AbortController();
      if (submit) submit.disabled = true;
      if (status) status.textContent = form.dataset.bdrLoading || 'Calculating shipping rates…';
      if (results) {
        results.hidden = true;
        results.replaceChildren();
      }

      try {
        const prepare = await fetch(`${root}cart/prepare_shipping_rates.json?${params}`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          signal: this.shippingController.signal,
        });
        if (!prepare.ok) throw new Error(`Shipping prepare ${prepare.status}`);

        let rates = null;
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await new Promise((resolve) => window.setTimeout(resolve, attempt === 0 ? 200 : 500));
          const response = await fetch(`${root}cart/async_shipping_rates.json?${params}`, {
            headers: { Accept: 'application/json' },
            signal: this.shippingController.signal,
          });
          if (!response.ok) throw new Error(`Shipping rates ${response.status}`);
          const text = await response.text();
          const payload = text ? JSON.parse(text) : null;
          if (payload && Array.isArray(payload.shipping_rates)) {
            rates = payload.shipping_rates;
            break;
          }
        }

        if (!rates) throw new Error('Shipping rates timeout');
        this.renderShippingRates(results, rates);
        if (status) status.textContent = rates.length ? '' : (form.dataset.bdrEmpty || 'No rates available.');
      } catch (error) {
        if (error.name !== 'AbortError' && status) status.textContent = form.dataset.bdrError || 'Unable to calculate shipping rates.';
      } finally {
        if (submit) submit.disabled = false;
      }
    }

    renderShippingRates(container, rates) {
      if (!container || !rates.length) return;
      const formatter = new Intl.NumberFormat(this.dataset.bdrLocale || document.documentElement.lang || 'fr', {
        style: 'currency',
        currency: this.dataset.bdrCurrency || 'EUR',
      });
      const fragment = document.createDocumentFragment();
      rates.forEach((rate) => {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name || rate.code || '';
        const numericPrice = Number.parseFloat(rate.price);
        price.textContent = Number.isFinite(numericPrice) ? formatter.format(numericPrice) : String(rate.price || '');
        item.append(name, price);
        fragment.appendChild(item);
      });
      container.replaceChildren(fragment);
      container.hidden = false;
    }

    async handleClick(event) {
      const target = event.target.closest('[data-bdr-qty-change], [data-bdr-line-remove], [data-bdr-upsell-add], [data-bdr-upsell-scroll], [data-bdr-close]');
      if (!target) return;

      if (target.hasAttribute('data-bdr-close')) {
        this.close();
        return;
      }

      if (target.hasAttribute('data-bdr-qty-change')) {
        const itemEl = target.closest('[data-bdr-line-key]');
        if (!itemEl) return;
        const lineKey = itemEl.dataset.bdrLineKey;
        const currentQty = parseInt(itemEl.dataset.bdrQty || '1', 10);
        const change = parseInt(target.dataset.bdrQtyChange, 10);
        const newQty = Math.max(0, currentQty + change);

        try {
          await window.Bedrock.changeCart({ id: lineKey, quantity: newQty });
        } catch (e) {
          console.error(e);
        }
      }

      if (target.hasAttribute('data-bdr-line-remove')) {
        const itemEl = target.closest('[data-bdr-line-key]');
        if (!itemEl) return;
        try {
          await window.Bedrock.changeCart({ id: itemEl.dataset.bdrLineKey, quantity: 0 });
        } catch (e) {
          console.error(e);
        }
      }

      if (target.hasAttribute('data-bdr-upsell-scroll')) {
        const track = this.querySelector('[data-bdr-upsell-track]');
        const card = track?.firstElementChild;
        if (!track || !card) return;
        const step = card.getBoundingClientRect().width + 12;
        track.scrollBy({ left: step * parseInt(target.dataset.bdrUpsellScroll, 10), behavior: 'smooth' });
      }

      if (target.hasAttribute('data-bdr-upsell-add')) {
        const variantId = target.dataset.bdrUpsellAdd;
        try {
          await window.Bedrock.addToCart([{ id: variantId, quantity: 1 }]);
        } catch (e) {
          console.error(e);
        }
      }
    }
  }

  customElements.define('bdr-drawer-cart', BdrDrawerCart);
})();
